/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState, useContext } from 'react';
import { Form, Field } from 'react-final-form';
import { Button } from '@blend/alchemy';
import styled from 'styled-components';
import { Box } from 'grommet';

import { Text, AlertBar } from '@blend/enterprise-components';
import { Modal } from 'blb-components/GrommetComponents';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';
import { BlendBridge } from 'blb-types/blendBridge';
import { getPersona } from 'blb-persona/index';
import { MeContext } from 'blb-contexts/Me';
import { isAdminInputConfig } from './helpers';
import { MemberFields } from './styles';
import { some } from 'lodash';

import { patchTeamMembers } from 'blb-api/groups';

const ModalStyles = {
  content: {
    height: 'fit-content',
    width: '500px',
  },
};

const Row = styled(Box)`
  display: grid;
  grid-template-columns: 30ch 5ch;
  align-items: center;
  gap: 0 12px;
  margin-bottom: 8px;
`;

const AlertBarContainer = styled(Box)`
  margin-bottom: 16px;
`;

interface EditTeamMemberModalProps {
  handleClose: (shouldRefetch?: boolean) => Promise<void>;
  team: BlendBridge.API.SearchGroupRecord;
  assignment: BlendBridge.API.GroupAssignmentWithName;
}

interface EditMemberFormValues {
  isAdmin: boolean;
}

const EditTeamMemberModal: React.FC<EditTeamMemberModalProps> = ({ handleClose, team, assignment }) => {
  const [error, setError] = useState<Error | undefined>();
  const me = useContext(MeContext)!;

  const activeAdmins = team.assignments.filter(
    ({ role, status }) =>
      role === BlendBridge.API.GroupRoles.ADMIN && status === BlendBridge.API.GroupInvitationStatuses.ACCEPTED,
  );
  const isLastAdmin = activeAdmins.length === 1 && some(activeAdmins, { principalId: assignment.principalId });

  const Toaster = useToasts()!;

  const initialValues: EditMemberFormValues = {
    isAdmin: assignment.role === BlendBridge.API.GroupRoles.ADMIN,
  };

  const handleCloseModal = async () => {
    setError(undefined);
    await handleClose(true);
  };

  const onSubmit = async (formValues: EditMemberFormValues) => {
    setError(undefined);

    try {
      const { isAdmin } = formValues;
      const role = isAdmin ? BlendBridge.API.GroupRoles.ADMIN : BlendBridge.API.GroupRoles.MEMBER;

      await patchTeamMembers(team.id, [
        {
          username: assignment.username,
          role,
          principalType: assignment.principalType,
        },
      ]);
      Toaster.addToast({
        message: `${assignment.name} is ${isAdmin ? 'now' : 'no longer'} an admin of ${team.name}.`,
        theme: 'info',
      });
      await handleCloseModal();
    } catch (err) {
      setError(err as Error);
    }
  };

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, submitting }) => {
        return (
          <Modal
            buttonComponents={
              <Button
                disabled={submitting || isLastAdmin}
                id="it-edit-team-member-submit"
                label={'Save'}
                onClick={handleSubmit}
                primary
              />
            }
            handleClose={handleCloseModal}
            id="edit-team-member-modal"
            isOpen
            style={ModalStyles}
            title={'Edit team member'}
          >
            {error ? (
              <AlertBarContainer>
                <AlertBar
                  icon="error"
                  id="it-edit-member-error"
                  message={`Error while trying to edit team member info. Please try again or contact support at ${
                    getPersona(me.principalType).getSupportContactInfo().email
                  }`}
                  theme="error"
                />
              </AlertBarContainer>
            ) : null}
            {isLastAdmin ? (
              <AlertBarContainer>
                <AlertBar
                  id="it-edit-member-admin-removal-warning"
                  message="This user is the only admin in the team so their admin privileges cannot be removed."
                  theme="warn"
                />
              </AlertBarContainer>
            ) : null}
            <Text fontSize="md" weight="semibold">
              Admin
            </Text>
            <Text fontSize="md">
              Admins have the ability to edit team information, add/remove team members and admins, and delete the team.
            </Text>
            <MemberFields>
              <li>
                <Row>
                  <Text fontSize="sm" weight="semibold">
                    Name
                  </Text>
                  <Text fontSize="sm" weight="semibold">
                    Admin
                  </Text>
                </Row>
              </li>
              <li>
                <Row>
                  <Text fontSize="md">{assignment.name}</Text>
                  <Field
                    {...isAdminInputConfig}
                    disabled={isLastAdmin}
                    id={`it-checkbox-isAdmin-${assignment.principalId}`}
                  />
                </Row>
              </li>
            </MemberFields>
          </Modal>
        );
      }}
    ></Form>
  );
};

export default EditTeamMemberModal;
